import axios from 'axios';
import { Dispatch } from 'redux';
import { RootState } from '../store'; // Assuming you have a RootState type for your Redux store
import api from './api';

// Action Types
const ADD_REVIEW_REQUEST = 'ADD_REVIEW_REQUEST';
const ADD_REVIEW_SUCCESS = 'ADD_REVIEW_SUCCESS';
const ADD_REVIEW_FAILURE = 'ADD_REVIEW_FAILURE';
const GET_REVIEWS_REQUEST = 'GET_REVIEWS_REQUEST';
const GET_REVIEWS_SUCCESS = 'GET_REVIEWS_SUCCESS';
const GET_REVIEWS_FAILURE = 'GET_REVIEWS_FAILURE';
const CHECK_CAN_COMMENT_REQUEST = 'CHECK_CAN_COMMENT_REQUEST';
const CHECK_CAN_COMMENT_SUCCESS = 'CHECK_CAN_COMMENT_SUCCESS';
const CHECK_CAN_COMMENT_FAILURE = 'CHECK_CAN_COMMENT_FAILURE';
const GET_REVIEWS_BY_CITY_REQUEST = 'GET_REVIEWS_BY_CITY_REQUEST';
const GET_REVIEWS_BY_CITY_SUCCESS = 'GET_REVIEWS_BY_CITY_SUCCESS';
const GET_REVIEWS_BY_CITY_FAILURE = 'GET_REVIEWS_BY_CITY_FAILURE';
const GET_RANDOM_REVIEWS_REQUEST = 'GET_RANDOM_REVIEWS_REQUEST';
const GET_RANDOM_REVIEWS_SUCCESS = 'GET_RANDOM_REVIEWS_SUCCESS';
const GET_RANDOM_REVIEWS_FAILURE = 'GET_RANDOM_REVIEWS_FAILURE';

// Action Interfaces
interface AddReviewRequestAction {
  type: typeof ADD_REVIEW_REQUEST;
}

interface AddReviewSuccessAction {
  type: typeof ADD_REVIEW_SUCCESS;
  payload: ReviewState;
}

interface AddReviewFailureAction {
  type: typeof ADD_REVIEW_FAILURE;
  error: string;
}

interface GetReviewsRequestAction {
  type: typeof GET_REVIEWS_REQUEST;
}

interface GetReviewsSuccessAction {
  type: typeof GET_REVIEWS_SUCCESS;
  payload: {
    reviews: ReviewState[];
    rating: number;
    totalReviews: number;
    pagination: {
      totalPages: number;
      currentPage: number;
      limit: number;
    };
  };
}

interface GetReviewsFailureAction {
  type: typeof GET_REVIEWS_FAILURE;
  error: string;
}

interface CheckCanCommentRequestAction {
  type: typeof CHECK_CAN_COMMENT_REQUEST;
}

interface CheckCanCommentSuccessAction {
  type: typeof CHECK_CAN_COMMENT_SUCCESS;
  payload: boolean; // True if the user can comment, false otherwise
}

interface CheckCanCommentFailureAction {
  type: typeof CHECK_CAN_COMMENT_FAILURE;
  error: string;
}

interface GetReviewsByCityRequestAction {
  type: typeof GET_REVIEWS_BY_CITY_REQUEST;
}

interface GetReviewsByCitySuccessAction {
  type: typeof GET_REVIEWS_BY_CITY_SUCCESS;
  payload: ReviewState[];
  pagination: {
    totalReviews: number;
    totalPages: number;
    currentPage: number;
    limit: number;
  };
}

interface GetReviewsByCityFailureAction {
  type: typeof GET_REVIEWS_BY_CITY_FAILURE;
  error: string;
}

interface GetRandomReviewsRequestAction {
  type: typeof GET_RANDOM_REVIEWS_REQUEST;
}

interface GetRandomReviewsSuccessAction {
  type: typeof GET_RANDOM_REVIEWS_SUCCESS;
  payload: ReviewState[];
}

interface GetRandomReviewsFailureAction {
  type: typeof GET_RANDOM_REVIEWS_FAILURE;
  error: string;
}


type ReviewActions =
  | AddReviewRequestAction
  | AddReviewSuccessAction
  | AddReviewFailureAction
  | GetReviewsRequestAction
  | GetReviewsSuccessAction
  | GetReviewsFailureAction
  | CheckCanCommentRequestAction
  | CheckCanCommentSuccessAction
  | CheckCanCommentFailureAction
  | GetReviewsByCityRequestAction
  | GetReviewsByCitySuccessAction
  | GetReviewsByCityFailureAction
  | GetRandomReviewsRequestAction
  | GetRandomReviewsSuccessAction
  | GetRandomReviewsFailureAction;

// State Type
interface ReviewState {
    uuid: string;
    userUuid: string;
    tourUuid: string;
    rating: number;
    comment: string;
    createdAt: string;
    updatedAt: string;
    activated: boolean;
    name: string;
    username?: string;
    tour?: { name:string };
    picture?:string;
    cityTranslated?:string;
    tourTranslated?:string;
}
  

interface ReviewsState {
  items: ReviewState[];
  loading: boolean;
  error: string | null;
  canComment: boolean | null;
  cityReviews: ReviewState[]; // ✅ New property for city-wide reviews
  cityReviewsPagination: {
    totalReviews: number;
    totalPages: number;
    currentPage: number;
    limit: number;
  } | null;
  cityReviewsLoading: boolean;
  cityReviewsError: string | null;
  tourRating: number | null;
  tourTotalReviews: number | null;
  tourPagination: {
    totalPages: number;
    currentPage: number;
    limit: number;
    totalReviews: number;
  } | null;
  randomReviews?: ReviewState[];
  randomReviewsLoading?: boolean;
  randomReviewsError?: string | null;
}

// Initial State
const initialState: ReviewsState = {
  items: [],
  loading: false,
  error: null,
  canComment: null,
  cityReviews: [], // ✅ New property
  cityReviewsPagination: null,
  cityReviewsLoading: false,
  cityReviewsError: null,
  tourRating: null,
  tourTotalReviews: null,
  tourPagination: null,
  randomReviews: [],
  randomReviewsLoading: false,
  randomReviewsError: null,
};

// Reducer
const reviewsReducer = (state: ReviewsState = initialState, action: ReviewActions): ReviewsState => {
  switch (action.type) {
    case ADD_REVIEW_REQUEST:
    case GET_REVIEWS_REQUEST:
    case CHECK_CAN_COMMENT_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };
    case ADD_REVIEW_SUCCESS:
      return {
        ...state,
        loading: false,
        items: [...state.items, action.payload],
      };
      case GET_REVIEWS_SUCCESS:
        return {
          ...state,
          loading: false,
          items: action.payload.reviews,
          tourRating: action.payload.rating,
          tourTotalReviews: action.payload.totalReviews,
          tourPagination: {
            ...action.payload.pagination,
            totalReviews: action.payload.totalReviews,
          }
      };     
    case CHECK_CAN_COMMENT_SUCCESS:
      return {
        ...state,
        loading: false,
        canComment: action.payload,
      };
    case ADD_REVIEW_FAILURE:
    case GET_REVIEWS_FAILURE:
    case CHECK_CAN_COMMENT_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.error,
      };
    case GET_REVIEWS_BY_CITY_REQUEST:
      return {
        ...state,
        cityReviewsLoading: true,
        cityReviewsError: null,
      };
    case GET_REVIEWS_BY_CITY_SUCCESS:
      return {
        ...state,
        cityReviews: action.payload,  // ✅ Assign action.payload directly (it's already an array)
        cityReviewsPagination: action.pagination,  // ✅ Use meta.pagination for pagination
        cityReviewsLoading: false,
        cityReviewsError: null,
      };
    case GET_REVIEWS_BY_CITY_FAILURE:
      return {
        ...state,
        cityReviewsLoading: false,
        cityReviewsError: action.error,
      }
    case GET_RANDOM_REVIEWS_REQUEST:
      return { ...state, randomReviewsLoading: true, randomReviewsError: null };

    case GET_RANDOM_REVIEWS_SUCCESS:
      return { ...state, randomReviewsLoading: false, randomReviews: action.payload };

    case GET_RANDOM_REVIEWS_FAILURE:
      return { ...state, randomReviewsLoading: false, randomReviewsError: action.error };

    default:
      return state;
  }
};

// Action Creators

export const getRandomReviews = (limit: number = 5, language: string) => async (dispatch: Dispatch) => {
  dispatch({ type: GET_RANDOM_REVIEWS_REQUEST });
  try {
    const response = await api.get(`/api/get-random-reviews`, {
      params: { limit: limit, language:language },
    });
    dispatch({ type: GET_RANDOM_REVIEWS_SUCCESS, payload: response.data.reviews });
  } catch (error) {
    let errorMessage = 'Failed to load reviews';
    if (axios.isAxiosError(error)) {
      errorMessage = error.response?.data?.message || error.message;
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }
    dispatch({ type: GET_RANDOM_REVIEWS_FAILURE, error: errorMessage });
  }
};


export const addReview = (review: ReviewState) => async (dispatch: Dispatch, getState: () => RootState) => {
  dispatch({ type: ADD_REVIEW_REQUEST });
  const userString = localStorage.getItem('user');
  let accessToken = '';
  if (userString) {
    const user = JSON.parse(userString);
    accessToken = user.access_token;
  }

  try {
    const response = await api.post(`/api/reviews/add`, review, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
    dispatch({ type: ADD_REVIEW_SUCCESS, payload: response.data });
  } catch (error) {
    let errorMessage = 'An unknown error occurred';
    if (axios.isAxiosError(error)) {
      errorMessage = error.response?.data?.message || error.message;
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }
    dispatch({ type: ADD_REVIEW_FAILURE, error: errorMessage });
  }
};

export const getReviews = (tourUuid: string, page: number = 1) => async (dispatch: Dispatch) => {
  dispatch({ type: GET_REVIEWS_REQUEST });
  const userString = localStorage.getItem('user');
  let accessToken = '';
  if (userString) {
    const user = JSON.parse(userString);
    accessToken = user.access_token;
  }

  try {
    const response = await api.get(`/api/reviews/${tourUuid}`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      params: { page }, // 👈 Pass page param
    });

    dispatch({
      type: GET_REVIEWS_SUCCESS,
      payload: {
        reviews: response.data.reviews,
        rating: response.data.rating,
        totalReviews: response.data.totalReviews,
        pagination: response.data.pagination, // 👈 Set pagination
      },
    });
  } catch (error) {
    // ... unchanged
  }
};


export const canComment = (userUuid: string, tourUuid: string) => async (dispatch: Dispatch) => {
  dispatch({ type: CHECK_CAN_COMMENT_REQUEST });
  const userString = localStorage.getItem('user');
  let accessToken = '';
  if (userString) {
    const user = JSON.parse(userString);
    accessToken = user.access_token;
  }

  try {
    const response = await api.get(`/api/reviews/can-comment/${userUuid}/${tourUuid}`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
    dispatch({ type: CHECK_CAN_COMMENT_SUCCESS, payload: response.data.canComment });
  } catch (error) {
    let errorMessage = 'An unknown error occurred';
    if (axios.isAxiosError(error)) {
      errorMessage = error.response?.data?.message || error.message;
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }
    dispatch({ type: CHECK_CAN_COMMENT_FAILURE, error: errorMessage });
  }
};

export const getReviewsByCity = (cityId: string, language: string = "en_US", page: number = 1, limit: number = 10) => 
  async (dispatch: Dispatch) => {
    dispatch({ type: GET_REVIEWS_BY_CITY_REQUEST });
    const userString = localStorage.getItem('user');
    let accessToken = '';
    if (userString) {
      const user = JSON.parse(userString);
      accessToken = user.access_token;
    }

    try {
      const response = await api.get(`/api/reviews-by-city`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        params: { cityId, language, page, limit },
      });
      dispatch({ type: GET_REVIEWS_BY_CITY_SUCCESS, payload: response.data.reviews, pagination: response.data.pagination });
    } catch (error) {
      let errorMessage = 'An unknown error occurred';
      if (axios.isAxiosError(error)) {
        errorMessage = error.response?.data?.message || error.message;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      dispatch({ type: GET_REVIEWS_BY_CITY_FAILURE, error: errorMessage });
    }
};

export default reviewsReducer;